import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | error | timeout
  const [kind, setKind] = useState("order");
  const [giftCode, setGiftCode] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let active = true;

    const poll = async () => {
      if (!active) return;
      if (polls.current >= MAX_POLLS) { setStatus("timeout"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.kind) setKind(data.kind);
        if (data.gift_code) setGiftCode(data.gift_code);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF7] p-6">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-xl border border-amber-100/60 p-10 max-w-md w-full text-center" data-testid="payment-result">
        {status === "checking" && (<>
          <Loader2 className="h-14 w-14 text-[#2AB7CA] mx-auto animate-spin" />
          <h1 className="text-2xl font-display font-bold mt-4 text-slate-900">Confirming your payment…</h1>
          <p className="text-slate-500 mt-2">Hang tight, this only takes a moment.</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
          <h1 className="text-3xl font-display font-bold mt-4 text-slate-900">
            {kind === "subscription" ? "You're subscribed! 🎬" : kind === "gift" ? "Gift ready! 🎁" : "Yay, you got it! 🎉"}
          </h1>
          <p className="text-slate-500 mt-2">
            {kind === "subscription" ? "Your Bubz + plan is active. Enjoy unlimited kids streaming!"
              : kind === "gift" ? "Share this code with the lucky family — they can redeem it on the plan screen."
              : "Your order is confirmed. The seller will be in touch."}
          </p>
          {kind === "gift" && giftCode && (
            <div className="mt-5 rounded-2xl border-2 border-[#9B5DE5]/30 bg-[#9B5DE5]/5 p-4" data-testid="gift-code-result">
              <div className="text-xs font-bold text-[#9B5DE5] uppercase tracking-wide">Gift code</div>
              <div className="mt-1 font-mono text-lg font-bold text-slate-900 tracking-wide select-all">{giftCode}</div>
              <button data-testid="copy-gift-code-btn" onClick={() => { navigator.clipboard?.writeText(giftCode); }}
                className="mt-2 text-sm font-semibold text-[#9B5DE5] hover:underline">Copy code</button>
            </div>
          )}
          <Button data-testid="back-to-market-btn" onClick={() => navigate(kind === "order" ? "/marketplace" : "/parent")}
            className="mt-6 rounded-full bg-[#FF5A79] hover:bg-[#e04865] font-bold px-6 h-12">
            {kind === "order" ? "Back to Marketplace" : "Back to Parent Zone"}
          </Button>
        </>)}
        {(status === "error" || status === "timeout") && (<>
          <XCircle className="h-16 w-16 text-rose-400 mx-auto" />
          <h1 className="text-2xl font-display font-bold mt-4 text-slate-900">
            {status === "timeout" ? "Still processing…" : "Payment not completed"}
          </h1>
          <p className="text-slate-500 mt-2">
            {status === "timeout" ? "Your payment is taking longer than usual. Check back shortly." : "No charge was made. Please try again."}
          </p>
          <Button data-testid="back-to-market-btn" onClick={() => navigate("/marketplace")}
            className="mt-6 rounded-full bg-slate-900 hover:bg-slate-800 font-bold px-6 h-12">Back to Marketplace</Button>
        </>)}
      </motion.div>
    </div>
  );
}
